// @ts-check
/**
 * CommunicationRawView.js (Factory Pattern)
 *
 * Specialized raw view for EmailCommunication-Conversation.
 */

/** @typedef {import('../../../../stores/referenceUnitStore').ReferenceUnitHandle} ReferenceUnitHandle */

/**
 * @param {any} deps - Injected Vue/store dependencies, see componentRegistry.js.
 *   Typed as `any` to preserve the factory-pattern escape hatch for
 *   dynamically-loaded configurable types.
 */
export default function createComponent(deps) {
    const {
        ref,
        watch,
        onMounted,
        useReferenceUnitStore,
        useMatterStore,
        CommunicationEventItem,
    } = deps;

    return {
        name: 'CommunicationRawView',

        components: {
            CommunicationEventItem,
        },

        props: {
            reference: {
                type: Object,
                required: true,
            },
        },

        /** @param {{ reference: ReferenceUnitHandle }} props */
        setup(props) {
            const referenceUnitStore = useReferenceUnitStore();
            const matterStore = useMatterStore();

            const loading = ref(false);
            const error = ref(null);
            const rawViewModel = ref(null);

            async function fetchRawViewModel() {
                if (!props.reference) {
                    console.warn('[CommunicationRawView] No reference provided');
                    return;
                }

                // Serve a cached raw-view model synchronously (re-selecting or
                // navigating back to a reference is then instant, no refetch /
                // loading flash).
                const matterId = matterStore.currentMatter?.RecordId;
                const cached = matterId
                    ? referenceUnitStore.getCachedRawViewModel(props.reference, matterId)
                    : null;
                if (cached) {
                    rawViewModel.value = cached;
                    error.value = null;
                    loading.value = false;
                    return;
                }

                loading.value = true;
                error.value = null;

                try {
                    console.log('[CommunicationRawView] Fetching raw view for:', props.reference);

                    rawViewModel.value = await referenceUnitStore.fetchRawViewModel(
                        props.reference,
                        matterStore.currentMatter.RecordId,
                    );

                    console.log(
                        '[CommunicationRawView] Loaded raw view model:',
                        rawViewModel.value,
                    );
                } catch (err) {
                    console.error('[CommunicationRawView] Error fetching raw view:', err);
                    error.value = err instanceof Error ? err.message : String(err);
                } finally {
                    loading.value = false;
                }
            }

            onMounted(() => {
                fetchRawViewModel();
            });

            watch(
                () => props.reference,
                () => {
                    fetchRawViewModel();
                },
            );

            /** @param {string} eventId */
            const isHitEvent = (eventId) => {
                if (!rawViewModel.value || !rawViewModel.value.hit_event_ids) return false;
                return rawViewModel.value.hit_event_ids.includes(eventId);
            };

            /** @param {string} eventId */
            const getEventRecipients = (eventId) => {
                if (!rawViewModel.value || !rawViewModel.value.recipients_by_event) return [];
                return rawViewModel.value.recipients_by_event[eventId] || [];
            };

            return {
                loading,
                error,
                rawViewModel,
                isHitEvent,
                getEventRecipients,
            };
        },

        template: `
            <div class="communication-raw-view-content">
                <!-- Loading state -->
                <div v-if="loading" class="loading-state">
                    <div class="loading-dots"><span></span><span></span><span></span></div>
                </div>

                <!-- Error state -->
                <div v-else-if="error" class="error-state">
                    <p>Error loading conversation: {{ error }}</p>
                </div>

                <!-- Email thread -->
                <div v-else-if="rawViewModel" class="email-thread">
                    <CommunicationEventItem
                        v-for="event in rawViewModel.events"
                        :key="event.id"
                        :event="event"
                        :isHit="isHitEvent(event.id)"
                        :recipients="getEventRecipients(event.id)"
                    />

                    <!-- Empty state -->
                    <div v-if="rawViewModel.events.length === 0" class="empty-state">
                        No events found in this conversation.
                    </div>
                </div>
            </div>
        `,
    };
}
